import React, { useState, useEffect } from "react";
import {
  EyeOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../../firebase-config";
import { useAuth } from "../../../../provider/Auth.provider";
import { findUserByUID } from "../../../../service/findUsers/findUserByUID";
import { getCurrentActiveModule } from "../../../../service/modules/getCurrentActiveModule";
// import { useLiveModuleUpdater } from '../../../../service/modules/useLiveModuleUpdater';
import { notification } from "antd";
import { useNavigate } from "react-router-dom";
import styles from "./MyPackage.module.css";

// Function to get course IDs of student (same as in Edu.js)
async function getCourseIdsOfStudent(student_uid) {
  try {
    if (!student_uid) {
      console.error("No student_uid provided");
      return [];
    }

    const userDocRef = doc(db, "users", student_uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const buyItemIdsArray = Array.isArray(userData.buyItemIdsArray)
        ? userData.buyItemIdsArray
        : [];

      return buyItemIdsArray
        .filter((item) => item && typeof item === "object" && item.buyItemId)
        .map((item) => item.buyItemId);
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
}

// Function to get payment status and enrollment date
function getPaymentStatus(packageId, userData) {
  if (!userData?.buyItemIdsArray) return "Unknown";

  const packageItem = userData.buyItemIdsArray.find(
    (item) => item?.buyItemId === packageId
  );

  return packageItem?.status || "Unknown";
}

function getEnrollmentDate(packageId, userData) {
  if (!userData?.buyItemIdsArray) return "Unknown";

  const packageItem = userData.buyItemIdsArray.find(
    (item) => item?.buyItemId === packageId
  );

  return packageItem?.enrollmentDate || "Unknown";
}

const MyPackage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [allPackages, setAllPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const tabs = [
    { id: "all", label: "All", count: 0 },
    { id: "live", label: "Live", count: 0 },
    { id: "digital", label: "Digital product", count: 0 },
  ];

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) {
        // notification.error({
        //     message: "User UID not found",
        //     description: "Please contact admin",
        //     placement: "topRight",
        //     duration: 3,
        // });
        setIsLoading(false);
        return;
      }

      try {
        const userData = await findUserByUID(user.uid);
        setUserData(userData);
      } catch (error) {
        notification.error({
          message: "Error fetching user data",
          description: "Please try again or contact admin",
          placement: "topRight",
          duration: 3,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user?.uid]);

  // Function to get total modules count for a package
  const getTotalModulesCount = async (pkg) => {
    try {
      let totalModules = 0;

      if (
        pkg.packageContainingCourses &&
        pkg.packageContainingCourses.length > 0
      ) {
        // For digital packages with courses, count modules from all courses
        for (const courseId of pkg.packageContainingCourses) {
          const courseRef = doc(db, "lms_course", courseId);
          const courseSnap = await getDoc(courseRef);
          if (courseSnap.exists()) {
            const courseData = courseSnap.data();
            if (courseData.courseContainingModules) {
              totalModules += courseData.courseContainingModules.length;
            }
          }
        }
      } else if (
        pkg.packageContainingBatches &&
        pkg.packageContainingBatches.length > 0
      ) {
        // For live packages with batches, count modules from all batches
        for (const batchId of pkg.packageContainingBatches) {
          if (typeof batchId === "string") {
            const batchRef = doc(db, "lms_batch", batchId);
            const batchSnap = await getDoc(batchRef);
            if (batchSnap.exists()) {
              const batchData = batchSnap.data();
              if (batchData.batchContainingModules) {
                totalModules += batchData.batchContainingModules.length;
              }
            }
          }
        }
      }

      return totalModules || 1;
    } catch (error) {
      console.error("Error counting modules:", error);
      return 1;
    }
  };

  // Fetch package data with dynamic module information
  useEffect(() => {
    const fetchPackageData = async () => {
      if (!user?.uid) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await findCoursesData(user.uid);

        // Fetch module counts and current active modules
        const packagesWithModules = await Promise.all(
          data.map(async (pkg) => {
            // Get total modules count
            const totalModules = await getTotalModulesCount(pkg);

            if (pkg.packageType === "package_lc") {
              const currentModule = await getCurrentActiveModule(pkg.packageId);
              return {
                ...pkg,
                totalModules,
                currentModule: currentModule
                  ? {
                      time: formatModuleTime(currentModule),
                      link: currentModule.liveLink || "No link available",
                      moduleName: currentModule.moduleName,
                      startDate: currentModule.startDate,
                      endDate: currentModule.endDate,
                    }
                  : {
                      time: "No active session",
                      link: "No link available",
                      moduleName: "No active module",
                    },
              };
            }

            return {
              ...pkg,
              totalModules,
            };
          })
        );

        setAllPackages(
          Array.isArray(packagesWithModules) ? packagesWithModules : []
        );
      } catch (error) {
        notification.error({
          message: "Error fetching packages",
          description: "Please try again or contact admin",
          placement: "topRight",
          duration: 3,
        });
        setAllPackages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPackageData();
  }, [user?.uid]);

  // Auto-update live modules every 5 minutes
  // useLiveModuleUpdater(allPackages, setAllPackages, 5);

  // Helper function to format module time
  const formatModuleTime = (module) => {
    if (!module || !module.startDate) return "No schedule available";

    const now = new Date();
    const startDate = new Date(module.startDate);
    const endDate = new Date(module.endDate);

    if (now >= startDate && now <= endDate) {
      return `Active until ${endDate.toLocaleDateString()}`;
    } else if (now < startDate) {
      return `Starts on ${startDate.toLocaleDateString()}`;
    } else {
      return "Session ended";
    }
  };

  async function findCoursesData(student_uid) {
    try {
      const buyItemIdsArray = await getCourseIdsOfStudent(student_uid);

      if (!buyItemIdsArray.length) {
        return [];
      }

      const lmsPackageCollection = collection(db, "lms_package");
      const packageQuery = query(
        lmsPackageCollection,
        where("packageId", "in", buyItemIdsArray)
      );

      const packageSnapshot = await getDocs(packageQuery);
      const packageDetails = packageSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return packageDetails;
    } catch (error) {
      return [];
    }
  }

  // Transform database data to component format
  const transformPackageData = (packages) => {
    return packages.map((pkg, index) => {
      const status = getPaymentStatus(pkg.packageId, userData);
      const enrollmentDate = getEnrollmentDate(pkg.packageId, userData);

      // Determine if it's live or digital based on package type
      const isLive = pkg.packageType === "package_lc";
      const isDigital = pkg.packageType === "package_dp";

      // Calculate total lessons from modules (both live and digital)
      const totalLessons = pkg.totalModules || 1;

      const progress = Math.floor(Math.random() * 100) + 1; // Placeholder - replace with actual progress
      const lessonsCompleted = Math.floor((progress / 100) * totalLessons);

      return {
        id: pkg.id, // Use document ID
        packageId: pkg.packageId, // Keep packageId field
        title: pkg.packageName || "Unnamed Package",
        instructor: pkg.packageInstructor || "Unknown Instructor",
        difficulty: pkg.packageDifficulty || "Intermediate",
        difficultyColor:
          pkg.packageDifficulty === "Advanced"
            ? "bg-red-500"
            : pkg.packageDifficulty === "Beginner"
            ? "bg-green-500"
            : "bg-yellow-500",
        duration: pkg.packageDuration || "4 weeks",
        progress: progress,
        lessonsCompleted: lessonsCompleted,
        totalLessons: totalLessons,
        status:
          status === "approved"
            ? "completed"
            : status === "pending"
            ? "ongoing"
            : "pending",
        statusColor:
          status === "approved"
            ? "bg-green-500"
            : status === "pending"
            ? "bg-blue-500"
            : "bg-yellow-500",
        thumbnail:
          pkg.packageBanner ||
          "https://via.placeholder.com/300x200/1f2937/ffffff?text=Course+Image",
        nextSession: isLive ? pkg.currentModule : null, // Will be populated with dynamic data
        lastAccessed: (() => {
          // Try to get actual last accessed date from user data or package data
          if (
            userData?.lastAccessedPackages &&
            userData.lastAccessedPackages[pkg.packageId]
          ) {
            const lastAccessDate = new Date(
              userData.lastAccessedPackages[pkg.packageId]
            );
            const now = new Date();
            const diffTime = Math.abs(now - lastAccessDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays === 1) return "1 day ago";
            if (diffDays < 7) return `${diffDays} days ago`;
            if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
            return `${Math.ceil(diffDays / 30)} months ago`;
          }

          // Fallback to enrollment date calculation
          if (enrollmentDate !== "Unknown") {
            const enrollDate = new Date(enrollmentDate);
            const now = new Date();
            const diffTime = Math.abs(now - enrollDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays === 1) return "1 day ago";
            if (diffDays < 7) return `${diffDays} days ago`;
            if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
            return `${Math.ceil(diffDays / 30)} months ago`;
          }

          return "Recently";
        })(),
        description:
          pkg.packageDescription ||
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
        actionText: isDigital ? "Continue Learning" : "View Details",
        actionIcon: <PlayCircleOutlined className="text-lg" />,
        type: isLive ? "live" : isDigital ? "digital" : "digital",
        packageType: pkg.packageType,
      };
    });
  };

  // Filter packages based on active tab
  const getFilteredPackages = () => {
    const transformedPackages = transformPackageData(allPackages);

    switch (activeTab) {
      case "live":
        return transformedPackages.filter((pkg) => pkg.type === "live");
      case "digital":
        return transformedPackages.filter((pkg) => pkg.type === "digital");
      default:
        return transformedPackages;
    }
  };

  const filteredPackages = getFilteredPackages();

  // Update tab counts
  const updatedTabs = tabs.map((tab) => {
    const transformedPackages = transformPackageData(allPackages);
    let count = 0;

    switch (tab.id) {
      case "live":
        count = transformedPackages.filter((pkg) => pkg.type === "live").length;
        break;
      case "digital":
        count = transformedPackages.filter(
          (pkg) => pkg.type === "digital"
        ).length;
        break;
      default:
        count = transformedPackages.length;
    }

    return { ...tab, count };
  });

  if (isLoading) {
    return (
      <div className={styles.myPackageContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading your packages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.myPackageContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>My Packages</h1>
        <p className={styles.subtitle}>Continue your learning journey</p>
      </div>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        {updatedTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`${styles.tab} ${
              activeTab === tab.id ? styles.activeTab : ""
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Package Cards */}
      {filteredPackages.length > 0 ? (
        <div className={styles.packagesGrid}>
          {filteredPackages.map((pkg) => (
            <div key={pkg.id} className={styles.packageCard}>
              {/* Thumbnail */}
              <div className={styles.thumbnailContainer}>
                <img
                  src={pkg.thumbnail}
                  alt={pkg.title}
                  className={styles.thumbnail}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/300x200/1f2937/ffffff?text=Course+Image";
                  }}
                />
                {/* Status Badge */}
                <div className={`${styles.statusBadge} ${styles[pkg.status]}`}>
                  {pkg.status}
                </div>
              </div>

              {/* Content */}
              <div className={styles.cardContent}>
                {/* Title and Instructor */}
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>{pkg.title}</h3>
                  <p className={styles.instructor}>{pkg.instructor}</p>
                </div>

                {/* Difficulty and Duration - Top position */}
                <div className={styles.cardMeta}>
                  <span
                    className={`${styles.difficulty} ${
                      styles[pkg.difficulty.toLowerCase()]
                    }`}
                  >
                    {pkg.difficulty}
                  </span>
                  <span className={styles.duration}>{pkg.duration}</span>
                </div>

                {/* Progress Section - Middle position */}
                <div className={styles.progressSection}>
                  <div className={styles.progressHeader}>
                    <span className={styles.progressLabel}>Progress</span>
                    <span className={styles.lessonCount}>
                      {pkg.lessonsCompleted}/{pkg.totalLessons} lessons
                    </span>
                  </div>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${pkg.progress}%` }}
                    ></div>
                  </div>
                  <div className={styles.progressPercentage}>
                    {pkg.progress}% complete
                  </div>
                </div>

                {/* Next Session or Last Accessed */}
                {pkg.nextSession ? (
                  <div className={styles.sessionInfo}>
                    <div className={styles.sessionLabel}>
                      <CalendarOutlined className={styles.calendarIcon} />
                      {pkg.nextSession.moduleName || "Live Session"}
                    </div>
                    <div className={styles.sessionTime}>
                      {pkg.nextSession.time}
                    </div>
                    <div className={styles.sessionLink}>
                      Join link:
                      {pkg.nextSession.link !== "No link available" ? (
                        <a
                          href={pkg.nextSession.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {pkg.nextSession.link}
                        </a>
                      ) : (
                        <span> {pkg.nextSession.link}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={styles.lastAccessed}>
                    Last accessed: {pkg.lastAccessed}
                  </div>
                )}

                {/* Description for digital packages */}
                {pkg.type === "digital" && (
                  <div className={styles.description}>
                    {(() => {
                      let description =
                        pkg.description || "Course description not available";

                      // Clean HTML content
                      description = description
                        .replace(/<[^>]*>/g, "") // Remove HTML tags
                        .replace(/&nbsp;/g, " ") // Replace non-breaking spaces
                        .replace(/&amp;/g, "&")
                        .replace(/&lt;/g, "<")
                        .replace(/&gt;/g, ">")
                        .replace(/&quot;/g, '"')
                        .replace(/&#39;/g, "'")
                        .trim();

                      const isExpanded = expandedDescriptions[pkg.id];
                      const words = description.split(" ");
                      const shouldTruncate = words.length > 30;
                      const displayText =
                        isExpanded || !shouldTruncate
                          ? description
                          : words.slice(0, 30).join(" ") + "...";

                      return (
                        <>
                          <div
                            className={`${styles.descriptionText} ${
                              !isExpanded && shouldTruncate
                                ? styles.truncated
                                : ""
                            }`}
                          >
                            {displayText}
                          </div>
                          {shouldTruncate && (
                            <button
                              className={styles.seeMoreBtn}
                              onClick={() =>
                                setExpandedDescriptions((prev) => ({
                                  ...prev,
                                  [pkg.id]: !prev[pkg.id],
                                }))
                              }
                            >
                              {isExpanded ? "See less" : "See more"}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Action Button */}
                <button
                  className={styles.actionButton}
                  onClick={() => {
                    if (pkg.packageType === "package_dp") {
                      navigate(`/student/dashboard/courses/${pkg.id}`);
                    } else {
                      navigate(`/student/dashboard/mypackage/course/${pkg.id}`);
                    }
                  }}
                >
                  <PlayCircleOutlined className={styles.playIcon} />
                  {pkg.packageType === "package_dp"
                    ? "Continue Learning"
                    : "View Details"}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📚</div>
          <h2>No Packages Enrolled</h2>
          <p>You haven't enrolled in any packages yet.</p>
          <p>Browse our available packages to get started!</p>
        </div>
      )}
    </div>
  );
};

export default MyPackage;
